import { Play, RefreshCw } from "lucide-react";
import type { DashboardSchedule } from "../../services/schedules.service";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  formatScheduleTime,
  getLastRunText,
  getScheduleBadge,
} from "./schedule-ui";

type ScheduleRowProps = {
  isBusy: boolean;
  onRunNow: (schedule: DashboardSchedule) => void;
  onToggle: (schedule: DashboardSchedule, enabled: boolean) => void;
  schedule: DashboardSchedule;
};

export function ScheduleRow({
  isBusy,
  onRunNow,
  onToggle,
  schedule,
}: ScheduleRowProps) {
  const badge = getScheduleBadge(schedule);
  const lastRun = getLastRunText(schedule);

  return (
    <div className="grid gap-4 border-b px-5 py-4 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_220px_220px]">
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">{schedule.name}</h2>
          <Badge variant="outline" className={badge.className}>
            {badge.label}
          </Badge>
          <Badge variant="secondary" className="font-mono">
            {schedule.cronExpression}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{schedule.description}</p>
        {lastRun.text ? (
          <p
            className={`max-w-4xl break-words text-xs ${
              lastRun.isError ? "text-destructive" : "text-muted-foreground"
            }`}
          >
            {lastRun.text}
          </p>
        ) : null}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs lg:block lg:space-y-2">
        <div>
          <dt className="text-muted-foreground">Next run</dt>
          <dd className="font-medium">
            {formatScheduleTime(schedule.nextRunAt)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Last run</dt>
          <dd className="font-medium">
            {formatScheduleTime(schedule.lastRunAt)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Runs</dt>
          <dd className="font-medium">
            {schedule.successCount} ok / {schedule.failureCount} failed
          </dd>
        </div>
      </dl>

      <div className="flex items-center justify-between gap-3 lg:justify-end">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Enabled</span>
          <Switch
            checked={schedule.enabled}
            disabled={isBusy}
            onCheckedChange={(enabled) => onToggle(schedule, enabled)}
            aria-label={`Toggle ${schedule.name}`}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={isBusy || schedule.running}
          onClick={() => onRunNow(schedule)}
        >
          {isBusy ? (
            <RefreshCw className="size-4 animate-spin" />
          ) : (
            <Play className="size-4" />
          )}
          Run now
        </Button>
      </div>
    </div>
  );
}
